import React, { useState } from 'react'
import PropTypes from 'prop-types'
import useQuery from '@/hooks/useQuery'

import { ShareIcon, DuplicateIcon } from '@heroicons/react/solid'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { useGlobalState } from '@/state'
import { VideoPlayer } from '@/pages/Users/PublicProfile/VideoPlayer'
import DefaultButton from '@/shared/Buttons/Default'
import useModal from '@/hooks/useModalV2'
import Loading from '@/shared/Loading'
import Modal from '@/shared/Modal'
import VideomancyPrompt from '@/pages/SubmissionUpload/VideomancyPrompt'

const SubmissionShare = ({ submission }) => {
  const { openModal, closeModal, isOpen } = useModal()
  const [videoUrl, setVideoUrl] = useState(submission.videoUrl)
  const { putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')

  const maybeCreateVideo = () => {
    openModal()
  }

  const download = () => {
    fetch(videoUrl)
      .then((response) => response.blob())
      .then(blob => {
        const objectURL = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = objectURL
        anchor.download = `${submission.id}.mp4`
        anchor.target = '_blank'
        anchor.click()
        anchor.remove()
      })
  }

  const reload = () => { window.location.reload() }

  const showCopiedNotification = () => {
    setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
      <p className="text-sm font-medium dark:text-green-300 text-green-600">Copied</p>
      <p className="mt-1 text-sm dark:text-green-300 text-green-400">You did it! It's copied. You can paste it anywhere you want now.</p>
    </div>)
  }
  return <>
    <button type="button" className="flex space-x-1 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 items-center" onClick={maybeCreateVideo}>
      <ShareIcon className="h-5 w-5" aria-hidden="true" />
      <span className="text-xs hidden sm:block">Share</span>
    </button>
    <Modal isOpen={isOpen} full={true} closeModal={closeModal} >
      <div className="flex flex-col w-full text-center justify-center">
        <h2 className="mb-8 text-xs font-semibold tracking-widest text-gray-800 dark:text-gray-200 uppercase title-font">Share this submission! </h2>
        <CopyToClipboard text={`https://castingcall.club/s/${submission.id}`} onCopy={showCopiedNotification} >
          <span className='flex flex-col sm:flex-row justify-center items-center cursor-pointer p-2'>
            <DuplicateIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" aria-hidden="true" /> <span>Copy Link </span>
            <pre className='bg-gray-50 dark:bg-gray-800 ml-2'>{`https://www.castingcall.club/s/${submission.id}`}</pre>
          </span>
        </CopyToClipboard>
        <div className='flex flex-center p-5 space-x-4 justify-center items-center'>
          <a rel="noreferrer" target="_blank" href={`https://x.com/intent/tweet?url=https://www.castingcall.club/s/${submission.id}&text=${encodeURIComponent(`${submission.projectName} submission via @CastingCallClub`)}`}>
            <span className="sr-only" hidden>X</span>
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>

          <a rel="noreferrer" target="_blank" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://www.castingcall.club/s/${submission.id}`)}`}>
            <span className="sr-only" hidden>Facebook</span>
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
            </svg>
          </a>

          <a rel="noreferrer" target="_blank" href={`https://bsky.app/intent/compose?text=${encodeURIComponent(`${submission.projectName} submission via @CastingCallClub https://www.castingcall.club/s/${submission.id}`)}`}>
            <span className="sr-only" hidden>Bluesky</span>
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 600 530" aria-hidden="true">
              <path d="M135.72 44.03C202.216 93.951 273.74 195.17 300 249.49c26.262-54.316 97.782-155.54 164.28-205.46C512.26 8.009 590 -19.862 590 68.825c0 17.712-10.155 148.79-16.111 170.07-20.703 73.984-96.144 92.854-163.25 81.433 117.3 19.964 147.14 86.092 82.697 152.22-122.39 125.59-175.91-31.511-189.63-71.766-2.514-7.38-3.69-10.832-3.708-7.896-0.018-2.936-1.194 0.516-3.708 7.896-13.706 40.255-67.225 197.36-189.63 71.766-64.444-66.128-34.605-132.26 82.697-152.22-67.108 11.421-142.55-7.45-163.25-81.433C20.156 217.613 10 86.535 10 68.825c0-88.687 77.742-60.816 125.72-24.795z" />
            </svg>
          </a>
        </div>

        <div className='flex flex-col items-center px-5 pb-5 text-sm text-gray-600 dark:text-gray-300'>
          <p className='mb-3'>Don't forget to share with your followers on:</p>
          <div className='flex space-x-6 items-center'>
            <span className='flex flex-col items-center text-xs'>
              <svg className="h-6 w-6 mb-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              Instagram
            </span>
            <span className='flex flex-col items-center text-xs'>
              <svg className="h-6 w-6 mb-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
              </svg>
              TikTok
            </span>
            <span className='flex flex-col items-center text-xs'>
              <svg className="h-6 w-6 mb-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              YouTube
            </span>
          </div>
        </div>
        { !videoUrl && <VideomancyPrompt initialSubmission={submission} /> }
        { submission.videoUrl && <>
          <div className='container flex gap-y-4 justify-center w-1/2'>
            <VideoPlayer videoUrl={submission.videoUrl} thumbnailUrl={submission.videomancyThumbnailUrl} />
          </div>
          <div className='flex justify-center items-center w-md mt-4'>
            <DefaultButton onClick={download} text='Download Video' />
          </div>
        </> }
      </div>
    </Modal>
  </>
}
SubmissionShare.propTypes = {
  submission: PropTypes.object
}

export default SubmissionShare
